import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateRoute } from "@/store/slices/routesSlice";
import Modal from "../common/Modal";
import TextInput from "../common/TextInput";
import TextArea from "../common/TextArea";
import SelectDropdown from "../common/SelectDropdown";
import Button from "../common/Button";

const DIFFICULTY_OPTIONS = [
  { value: "", label: "Select Difficulty" },
  { value: "Easy", label: "Easy" },
  { value: "Medium", label: "Medium" },
  { value: "Hard", label: "Hard" },
];

const RouteEditModal = ({ open, onClose, route }) => {
  const dispatch = useDispatch();
  const { submitLoading } = useSelector((state) => state.routes);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    difficulty: "",
    start_point: "",
    end_point: "",
  });

  // Pre-fill form when modal opens with a route
  useEffect(() => {
    if (open && route) {
      setFormData({
        name: route.name || "",
        description: route.description || "",
        difficulty: route.difficulty || "",
        start_point: route.startPoint || "",
        end_point: route.endPoint || "",
      });
    }
  }, [open, route]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (fieldName) => (e) => {
    setFormData((prev) => ({ ...prev, [fieldName]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.warn("Please enter a route name");
      return;
    }
    if (!formData.description.trim()) {
      toast.warn("Please enter a description");
      return;
    }
    if (!formData.difficulty) {
      toast.warn("Please select a difficulty level");
      return;
    }
    if (!formData.start_point.trim()) {
      toast.warn("Please enter a start point");
      return;
    }
    if (!formData.end_point.trim()) {
      toast.warn("Please enter an end point");
      return;
    }

    // Create payload with only fields that can be updated
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      difficulty: formData.difficulty,
      start_point: formData.start_point.trim(),
      end_point: formData.end_point.trim(),
    };

    try {
      const result = await dispatch(
        updateRoute({ id: route.id, payload })
      ).unwrap();
      toast.success(result.message || "Route updated successfully!");
      onClose();
    } catch (err) {
      toast.error(err || "Failed to update route");
    }
  };

  if (!route) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Route"
      showCloseButton={false}
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto px-1"
      >
        <TextInput
          label="Route Name *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Mumbai Coastal Circuit"
          required
        />

        <TextArea
          label="Description *"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Brief description of the route"
          rows={3}
          required
        />

        <SelectDropdown
          label="Difficulty *"
          name="difficulty"
          options={DIFFICULTY_OPTIONS}
          value={formData.difficulty}
          onChange={handleSelectChange("difficulty")}
          required
        />

        <TextInput
          label="Start Point *"
          name="start_point"
          value={formData.start_point}
          onChange={handleChange}
          placeholder="e.g., Marine Drive"
          required
        />

        <TextInput
          label="End Point *"
          name="end_point"
          value={formData.end_point}
          onChange={handleChange}
          placeholder="e.g., Gateway of India"
          required
        />

        {/* Read-only info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <span className="font-medium">Note:</span> State and GPX file cannot
            be modified.
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            State: {route.stateCode}
          </p>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={submitLoading}
            disabled={submitLoading}
          >
            {submitLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RouteEditModal;
